import chevronDownIcon from '@jetbrains/icons/chevron-down.svg'
import chevronRightIcon from '@jetbrains/icons/chevron-right.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import type {ReactNode} from 'react'
import React, {memo, useMemo, useCallback, useContext, useRef, useState} from 'react'

import IconButton from '../../../../../../common/IconButton/IconButton'
import {DisabledContext} from '../../../../contexts/DisabledContext'
import useBooleanState from '../../../../hooks/useBooleanState'
import Actions from '../../Actions/Actions'
import Details from '../../Details/Details'
import EditSettingsForm from '../../EditSettingsForm/EditSettingsForm'

import styles from './SettingsBlockHeader.css'

import Input, {Size} from 'src/components/pages/PipelinesPages/components/common/Input'

type Props = {
  collapsed: boolean
  isDeleted?: boolean
  collapsible?: boolean
  collapsedNote?: ReactNode
  onChangeName?: (name: string) => void
  onDeleteClick?: () => void
  onRestoreClick?: () => void
  onAddClick?: () => void
  collapse: () => void
  expand: () => void
  regionId: string
  buttonId: string
  title: string | null | undefined
  generatedTitle: string | null | undefined
  details?: ReactNode
  rightElement?: ReactNode | ((editing: boolean) => ReactNode)
  updated?: boolean
  customActions?: (anchor: ReactNode) => ReactNode
  secondLine?: ReactNode
  draggable?: boolean
}

function SettingsBlockHeader({
  collapsed,
  isDeleted,
  collapsible,
  collapsedNote,
  onChangeName,
  onDeleteClick,
  onRestoreClick,
  onAddClick,
  collapse,
  expand,
  buttonId,
  regionId,
  title,
  generatedTitle,
  details,
  rightElement,
  customActions,
  secondLine,
  draggable,
}: Props) {
  const [isDetailsIconHover, setDetailsIconHover] = useState<boolean>(false)
  const [editing, enableEditing, disableEditing] = useBooleanState()
  const inputRef = useRef<HTMLInputElement>(null)
  const onEditClick = onChangeName != null ? enableEditing : undefined
  const isDisabled = useContext(DisabledContext)
  const actionsHandlers = useMemo(
    () => [onAddClick, onEditClick, onDeleteClick].filter(action => !!action),
    [onAddClick, onDeleteClick, onEditClick],
  )
  const isTitleUnionAction = useMemo(
    () => actionsHandlers.length === 1 && !collapsible,
    [actionsHandlers, collapsible],
  )
  const titleLabel = !editing ? (
    <span
      className={classNames({
        [styles.deleted]: isDeleted,
      })}
    >
      {[generatedTitle, title].filter(Boolean).join(': ')}
    </span>
  ) : null
  const confirmNameChange = useCallback(() => {
    disableEditing()
    if (onChangeName != null && inputRef.current?.value != null) {
      onChangeName(inputRef.current.value)
    }
  }, [onChangeName, disableEditing])
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        confirmNameChange()
        return
      }

      if (event.key === 'Escape') {
        disableEditing()
      }
    },
    [confirmNameChange, disableEditing],
  )
  const hoverDetailsIconHandler = (state: boolean) => setDetailsIconHover(state)
  const actionButtons = useMemo(() => {
    const actionButtons = (
      <Actions
        className={styles.actions}
        iconClassName={styles.actionIcon}
        deleted={isDeleted}
        disabled={isDisabled}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        onRestoreClick={onRestoreClick}
        onAddClick={onAddClick}
      />
    )
    return customActions ? customActions(actionButtons) : actionButtons
  }, [customActions, isDeleted, isDisabled, onAddClick, onDeleteClick, onEditClick, onRestoreClick])
  const onClickTitle = useMemo(() => {
    const [actionHandler] = actionsHandlers
    if (collapsible) {
      return collapsed ? expand : collapse
    }

    if (isTitleUnionAction) {
      return actionHandler
    }
    return () => {}
  }, [actionsHandlers, collapse, collapsed, collapsible, expand, isTitleUnionAction])

  return (
    <div
      id="addIntegrationElementId"
      className={classNames({[styles.collapsibleContainer]: collapsible})}
    >
      <div
        className={styles.header}
        onBlur={e => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            confirmNameChange()
          }
        }}
      >
        {draggable && (
          <IconButton
            data-movable-handle
            className={styles.handle}
            icon="drag"
            title="Use drag-and-drop to reorder steps"
          />
        )}
        <H2
          className={classNames(styles.title, {
            [styles.collapsible]: collapsible,
            [styles.titleUnion]: isTitleUnionAction,
          })}
          onClick={onClickTitle}
        >
          {collapsible ? (
            <Button
              disabled={isDeleted}
              id={buttonId}
              className={classNames(styles.toggle, {
                [styles.toggleWithEditing]: onChangeName != null,
                [styles.buttonUnhover]: isDetailsIconHover,
              })}
              icon={collapsed ? chevronRightIcon : chevronDownIcon}
              iconClassName={styles.toggleIcon}
              onClick={onClickTitle}
              title={`${collapsed ? 'Expand' : 'Collapse'} ${title || generatedTitle}`}
              aria-expanded={collapsed ? 'false' : 'true'}
              aria-controls={regionId}
            >
              {titleLabel}
              {!editing && details && (
                <Details onHover={hoverDetailsIconHandler}>{details}</Details>
              )}
            </Button>
          ) : (
            <React.Fragment>
              {titleLabel}
              {isTitleUnionAction && actionButtons}
            </React.Fragment>
          )}
        </H2>
        {editing && (
          <EditSettingsForm
            onConfirmClick={confirmNameChange}
            onCancelClick={disableEditing}
            inline
          >
            <Input
              className={styles.input}
              size={Size.FULL}
              inputRef={inputRef}
              defaultValue={title ?? ''}
              onKeyDown={onKeyDown}
              autoFocus
              aria-label="name"
            />
          </EditSettingsForm>
        )}
        {rightElement && typeof rightElement === 'function' ? rightElement(editing) : rightElement}
        {!editing && !isTitleUnionAction && actionButtons}
      </div>
      {collapsedNote && collapsed && <div className={styles.collapsedNote}>{collapsedNote}</div>}
      {secondLine}
    </div>
  )
}

export default memo<Props>(SettingsBlockHeader)
